import {
  AvailabilityResponse,
  StudiosResponse,
  CreateBookingResponse,
  BookingData,
} from "../types";
import { environment } from "../config/environment";
import { tokenManager } from "./tokenManager";

const API_BASE = environment.apiUrl;

/**
 * Make an authenticated API request with automatic token refresh
 */
async function makeAuthenticatedRequest(
  url: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<Response> {
  const maxRetries = 1; // Only retry once for token refresh

  try {
    // Get authorization header
    const authHeader = await tokenManager.getAuthorizationHeader();

    // Make the request with authorization header
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: authHeader,
        "Content-Type": "application/json",
      },
    });

    // If unauthorized and we haven't retried yet, try refreshing token
    if (response.status === 401 && retryCount < maxRetries) {
      console.log("Received 401, refreshing token and retrying...");

      // Clear current token and get a fresh one
      tokenManager.clearAll();
      const newAuthHeader = await tokenManager.getAuthorizationHeader();

      // Retry the request with new token
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: newAuthHeader,
          "Content-Type": "application/json",
        },
      });
    }

    return response;
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
}

export const getAvailability = async (
  startDate: string,
  endDate: string,
  studios: string[]
): Promise<AvailabilityResponse> => {
  try {
    const response = await makeAuthenticatedRequest(
      `${API_BASE}/availability?startDate=${startDate}&endDate=${endDate}&studios=${studios.join(
        ","
      )}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch availability");
    }

    return await response.json();
  } catch (error) {
    console.error("API Error (getAvailability):", error);
    throw error;
  }
};

export const getDayAvailability = async (
  date: string,
  studios: string[]
): Promise<AvailabilityResponse> => {
  try {
    const response = await makeAuthenticatedRequest(
      `${API_BASE}/availability?startDate=${date}&studios=${studios.join(",")}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch availability");
    }

    return await response.json();
  } catch (error) {
    console.error("API Error (getDayAvailability):", error);
    throw error;
  }
};

export const createBooking = async (
  bookingData: BookingData
): Promise<CreateBookingResponse> => {
  try {
    const response = await makeAuthenticatedRequest(`${API_BASE}/booking`, {
      method: "POST",
      body: JSON.stringify(bookingData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create booking");
    }

    return await response.json();
  } catch (error) {
    console.error("API Error (createBooking):", error);
    throw error;
  }
};

export const getStudios = async (): Promise<StudiosResponse> => {
  try {
    const response = await makeAuthenticatedRequest(`${API_BASE}/studios`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch studios");
    }

    return await response.json();
  } catch (error) {
    console.error("API Error (getStudios):", error);
    throw error;
  }
};
